import dayjs from "dayjs";

/**
 * Days left in the trial at which the banner switches to its urgent treatment.
 * Inclusive: with exactly this many days left the urgent version already shows.
 */
export const TRIAL_URGENT_THRESHOLD_DAYS = 5;

export interface IOfferAttractivenessTrialState {
  /** The lender is inside an unexpired free trial right now. */
  isActive: boolean;
  /**
   * Whole days left, counting today. The last day of the trial reads as 1, never 0 -
   * the end date from the backend is inclusive.
   */
  daysRemaining: number;
  /** Inside the trial AND at or below the urgent threshold. */
  isUrgent: boolean;
  /** Inclusive last day of the trial, or null when there is no trial. */
  endsOn: Date | null;
}

const INACTIVE: IOfferAttractivenessTrialState = {
  isActive: false,
  daysRemaining: 0,
  isUrgent: false,
  endsOn: null,
};

/**
 * Derive the banner's state from the lender-company payload.
 *
 * `isTrialActive` is authoritative for whether the trial is running - it is computed
 * server-side, so a lender whose clock is wrong still sees the right thing. The date is
 * used only to count down. Note the backend keeps sending `trialEndsOn` after the trial
 * expires, which is why its presence alone must never be read as "trialing".
 */
export function getOfferAttractivenessTrialState(
  isTrialActive: boolean | undefined,
  trialEndsOn: string | null | undefined,
): IOfferAttractivenessTrialState {
  if (!isTrialActive || !trialEndsOn) return INACTIVE;

  // Parse as a plain calendar date. `new Date("2026-10-31")` would parse as UTC midnight
  // and render as Oct 30 in any negative-offset zone; dayjs parses a date-only string as
  // local time instead. No format argument: it would be silently ignored without the
  // `customParseFormat` plugin, which reads as though it were doing work when it is not.
  const endsOn = dayjs(trialEndsOn).startOf("day");
  if (!endsOn.isValid()) return INACTIVE;

  const daysRemaining = endsOn.diff(dayjs().startOf("day"), "day") + 1;

  // The server says the trial is live, so treat a non-positive count as its final day
  // rather than contradicting the server on a clock skew.
  const safeDaysRemaining = Math.max(daysRemaining, 1);

  return {
    isActive: true,
    daysRemaining: safeDaysRemaining,
    isUrgent: safeDaysRemaining <= TRIAL_URGENT_THRESHOLD_DAYS,
    endsOn: endsOn.toDate(),
  };
}

/** Long-form end date for banner copy, e.g. "30 September 2026". */
export function formatTrialEndDate(endsOn: Date): string {
  return dayjs(endsOn).format("D MMMM YYYY");
}

/** Countdown copy, e.g. "29 days left" / "1 day left". */
export function formatDaysLeft(daysRemaining: number): string {
  return `${daysRemaining} ${daysRemaining === 1 ? "day" : "days"} left`;
}
